/**
 * Appels des recettes (specs §6.5, archi §4.3).
 *
 * ⚠️ **Aucune macro ne part d'ici.** Les types `RecipeInput` et
 * `UpdateRecipeBody` n'ont aucun champ où en écrire une, et le serveur refuserait
 * un corps qui en porterait un (400, corps `strict`). Les macros d'une recette
 * sont calculées à la lecture, depuis les ingrédients — c'est la règle cardinale
 * du §6.5, et ce fichier n'a rien à faire pour la respecter : il n'a simplement
 * rien à envoyer.
 *
 * ⚠️ Ce que renvoie l'API est **toujours recalculé** : `totals`, `perPortion`,
 * `per100` changent dès qu'un aliment ingrédient est corrigé, sans que personne
 * n'ait touché à la recette. C'est voulu (specs §6.5), et c'est exactement ce que
 * l'immuabilité du journal (§4.1) interdit dans l'autre sens : une entrée déjà
 * enregistrée, elle, ne bouge jamais.
 */

import type { RecipeDTO, RecipeInput, UpdateRecipeBody } from '../../../shared/types';
import { apiFetch } from './client';

function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

/** Clé de cache partagée — invalidée par toute écriture de recette. */
export const RECIPES_KEY = ['recipes'] as const;

/** Clé de la liste des tags, alimentée par `GET /api/recipes/tags`. */
export const RECIPE_TAGS_KEY = ['recipe-tags'] as const;

/**
 * Liste, recherche et filtrage (specs §6.5).
 *
 * La recherche porte sur le nom **et les tags**, insensible aux accents — côté
 * serveur, pour la même raison qu'aux aliments : `LIKE` ne sait pas le faire.
 */
export function fetchRecipes(query = '', tag?: string): Promise<RecipeDTO[]> {
  const params = new URLSearchParams({ q: query });
  if (tag !== undefined && tag !== '') params.set('tag', tag);
  return apiFetch<RecipeDTO[]>(`/api/recipes?${params.toString()}`);
}

/** Tous les tags employés, triés — de quoi peupler le filtre. */
export function fetchRecipeTags(): Promise<string[]> {
  return apiFetch<string[]>('/api/recipes/tags');
}

export function fetchRecipe(id: number): Promise<RecipeDTO> {
  return apiFetch<RecipeDTO>(`/api/recipes/${String(id)}`);
}

/** Création. Idempotente par `clientId` (règle non négociable n°9). */
export function createRecipe(body: RecipeInput): Promise<RecipeDTO> {
  return send<RecipeDTO>('POST', '/api/recipes', body);
}

/**
 * Correction d'une recette. **Sans effet sur le journal déjà enregistré** (§4.1) :
 * les blocs journalisés portent leur propre copie des lignes et des macros.
 */
export function updateRecipe(id: number, body: UpdateRecipeBody): Promise<RecipeDTO> {
  return send<RecipeDTO>('PATCH', `/api/recipes/${String(id)}`, body);
}

export function deleteRecipe(id: number): Promise<void> {
  return send<void>('DELETE', `/api/recipes/${String(id)}`);
}
